import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { IconComponent } from '../../public-api';

type PaginationItem = number | '...';

@Component({
    selector: 'nw-pagination',
    standalone: true,
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    imports: [IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginationComponent {
    @Input() totalItems = 0;
    @Input() pageSize = 10;
    @Input() currentPage = 1;
    @Input() maxVisiblePages = 5;

    @Output() pageChange = new EventEmitter<number>();

    get totalPages(): number {
        const pageSize = this.pageSize > 0 ? this.pageSize : 1;
        const totalItems = Math.max(0, this.totalItems);
        return Math.max(1, Math.ceil(totalItems / pageSize));
    }

    get pages(): PaginationItem[] {
        const total = this.totalPages;
        const current = this.currentPage;
        const max = this.maxVisiblePages;

        if (total <= max) {
            return Array.from({ length: total }, (_, index) => index + 1);
        }

        if (max < 5) {
            return [1, '...', total];
        }

        if (current <= max) {
            return [
                ...Array.from({ length: max }, (_, index) => index + 1),
                '...',
                total
            ];
        }

        if (current >= total - (max - 2)) {
            return [
                1,
                '...',
                ...Array.from(
                    { length: max - 1 },
                    (_, index) => total - (max - 2) + index
                )
            ];
        }

        return [
            1,
            '...',
            ...Array.from({ length: max - 1 }, (_, index) => current - (max - 3) + index),
            total
        ];
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages || page === this.currentPage) {
            return;
        }

        this.pageChange.emit(page);
    }

    trackByPage(index: number, item: PaginationItem): string {
        return `${item}-${index}`;
    }
}
